import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  MessageCircle,
  Pause,
  PhoneCall,
  Play,
  RefreshCw,
  Search,
  Send,
} from "lucide-react";
import { useBrand } from "@/lib/BrandContext";
import { detectBrandFromCenter } from "@/lib/brandConfig";
import { formatParisDate, formatParisTime } from "@/lib/parisDateTime";
import { supabase } from "@/lib/supabaseClient";
import { fetchAllByIds, fetchAllPages } from "@/lib/supabasePaging";

type Brand = "smile" | "laser" | "vertical";
type CycleStatus =
  | "active"
  | "waiting_staff"
  | "paused"
  | "completed"
  | "unsubscribed"
  | "rejected"
  | "error";

type BucketKey = "upcoming" | "due" | "waiting" | "whatsapp" | "stopped" | "errors";
type FollowUpView = "all" | "to_watch" | "manual" | "waiting" | "stopped";
type ReminderChannel = "email" | "whatsapp" | "phone";

interface CaseRow {
  id: string;
  title: string | null;
  company_name: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  center: string | null;
  brand: Brand | null;
  event_date: string | null;
  participants: number | null;
  pax: number | null;
  status: string;
}

interface CycleRow {
  id: string;
  case_id: string;
  thread_id: string | null;
  quote_ids: string[];
  upload_group_id: string | null;
  status: CycleStatus;
  current_step: number;
  next_step: number;
  next_reminder_at: string | null;
  last_quote_sent_at: string | null;
  last_client_response_at: string | null;
  last_staff_response_at: string | null;
  last_reminder_sent_at: string | null;
  last_error: string | null;
  whatsapp_task_due_at: string | null;
  whatsapp_status: "none" | "pending" | "sent" | "skipped";
  whatsapp_message: string | null;
  stopped_reason: string | null;
  rejection_reason: string | null;
  rejection_comment: string | null;
  unsubscribed_at: string | null;
  rejected_at: string | null;
  completed_at: string | null;
  paused_at: string | null;
  created_at: string;
  updated_at: string;
  cases: CaseRow | null;
}

interface ReminderLog {
  id: string;
  case_id: string;
  quote_id: string | null;
  channel: ReminderChannel;
  reminder_step: number | null;
  template_key: string | null;
  subject: string | null;
  email_sent: boolean | null;
  email_id: string | null;
  error_message: string | null;
  sent_at: string | null;
  created_at: string;
}

interface QuoteRow {
  id: string;
  display_name: string | null;
  version: number;
  signature_token: string | null;
  signed: boolean;
  signed_at: string | null;
  file_url: string;
  amount_ht: number | null;
}

interface CycleView extends CycleRow {
  caseRecord: CaseRow;
  logs: ReminderLog[];
  quotes: QuoteRow[];
  amountHt: number | null;
}

const BUCKETS: Array<{ key: BucketKey; label: string; description: string }> = [
  { key: "due", label: "À envoyer", description: "Email prêt à partir" },
  { key: "upcoming", label: "À venir", description: "Prévu plus tard" },
  { key: "waiting", label: "Réponse client", description: "Le client a répondu" },
  { key: "whatsapp", label: "WhatsApp programmé", description: "Envoi automatique prévu" },
  { key: "stopped", label: "Terminés", description: "Relances arrêtées" },
  { key: "errors", label: "Erreurs", description: "Envois à reprendre" },
];

const FOLLOW_UP_VIEWS: Array<{ key: FollowUpView; label: string }> = [
  { key: "all", label: "Tous" },
  { key: "to_watch", label: "À faire" },
  { key: "manual", label: "Appels" },
  { key: "waiting", label: "Réponse client" },
  { key: "stopped", label: "Terminés" },
];

const STEP_META: Record<number, { label: string; angle: string; templateKey: string }> = {
  1: {
    label: "R1",
    angle: "Message simple",
    templateKey: "quote_reminder_r1_classic_v1",
  },
  2: {
    label: "R2",
    angle: "Questions / ajustements",
    templateKey: "quote_reminder_r2_adjustments_v1",
  },
  3: {
    label: "R3",
    angle: "Aide + WhatsApp",
    templateKey: "quote_reminder_r3_decision_v1",
  },
  4: {
    label: "R4",
    angle: "Dernier message",
    templateKey: "quote_reminder_r4_last_call_v1",
  },
};

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  return `${formatParisDate(value)} ${formatParisTime(value)}`;
}

function formatMoney(value: number | null) {
  if (value === null) return "Montant non extrait";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

function getCycleBucket(cycle: CycleView, nowMs: number): BucketKey {
  if (cycle.status === "error") return "errors";
  if (
    cycle.whatsapp_status === "pending" &&
    cycle.whatsapp_task_due_at &&
    new Date(cycle.whatsapp_task_due_at).getTime() <= nowMs
  ) {
    return "whatsapp";
  }
  if (cycle.status === "waiting_staff") return "waiting";
  if (cycle.status === "completed" || cycle.status === "unsubscribed" || cycle.status === "rejected") {
    return "stopped";
  }
  if (
    cycle.status === "active" &&
    cycle.next_reminder_at &&
    new Date(cycle.next_reminder_at).getTime() <= nowMs
  ) {
    return "due";
  }
  return "upcoming";
}

function getEmailLogs(cycle: CycleView) {
  return cycle.logs.filter((log) => log.channel === "email");
}

function getSuccessfulEmailLogs(cycle: CycleView) {
  return getEmailLogs(cycle).filter((log) => log.email_sent);
}

function getPhoneLogs(cycle: CycleView) {
  return cycle.logs.filter((log) => log.channel === "phone" && log.email_sent);
}

function getWhatsappLogs(cycle: CycleView) {
  return cycle.logs.filter((log) => log.channel === "whatsapp" && log.email_sent);
}

function getLastContactLog(cycle: CycleView) {
  return [...cycle.logs]
    .filter((log) => log.sent_at || log.created_at)
    .sort(
      (left, right) =>
        new Date(right.sent_at || right.created_at).getTime() -
        new Date(left.sent_at || left.created_at).getTime()
    )[0] ?? null;
}

function getPhoneStep(cycle: CycleView) {
  return Math.min(Math.max(cycle.current_step || cycle.next_step || 1, 1), 4);
}

function getReminderState(cycle: CycleView, nowMs: number) {
  const bucket = getCycleBucket(cycle, nowMs);
  if (bucket === "errors") return { label: "Erreur", className: "bg-red-50 text-red-700 border-red-200" };
  if (bucket === "due") return { label: "Email à envoyer", className: "bg-amber-50 text-amber-800 border-amber-200" };
  if (bucket === "waiting") return { label: "Réponse client", className: "bg-sky-50 text-sky-700 border-sky-200" };
  if (bucket === "whatsapp") return { label: "WhatsApp programmé", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (bucket === "stopped") return { label: statusLabel(cycle.status), className: "bg-gray-100 text-gray-600 border-gray-200" };
  if (cycle.status === "paused") return { label: "En pause", className: "bg-gray-100 text-gray-700 border-gray-200" };
  return { label: "Prévu", className: "bg-indigo-50 text-indigo-700 border-indigo-200" };
}

function getCaseTitle(cycle: CycleView) {
  return (
    cycle.caseRecord.company_name ||
    cycle.caseRecord.title ||
    `${cycle.caseRecord.client_first_name || ""} ${cycle.caseRecord.client_last_name || ""}`.trim() ||
    "Dossier sans nom"
  );
}

function getClientName(cycle: CycleView) {
  return `${cycle.caseRecord.client_first_name || ""} ${cycle.caseRecord.client_last_name || ""}`.trim();
}

function getCycleBrand(cycle: CycleView) {
  return cycle.caseRecord.brand || detectBrandFromCenter(cycle.caseRecord.center);
}

function statusLabel(status: CycleStatus) {
  const labels: Record<CycleStatus, string> = {
    active: "Actif",
    waiting_staff: "Réponse client",
    paused: "En pause",
    completed: "Terminé",
    unsubscribed: "Stop demandé",
    rejected: "Refusé",
    error: "Erreur",
  };
  return labels[status];
}

function getStepLog(cycle: CycleView, step: number, channel: ReminderChannel = "email") {
  return cycle.logs.find((log) => log.reminder_step === step && log.channel === channel);
}

export default function RelancesPage() {
  const { brand } = useBrand();
  const [cycles, setCycles] = useState<CycleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCenter, setSelectedCenter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedView, setSelectedView] = useState<FollowUpView>("all");
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [savingAction, setSavingAction] = useState<string | null>(null);

  const fetchCycles = async () => {
    try {
      setLoading(true);
      setError(null);

      const cycleData = await fetchAllPages<CycleRow>((from, to) =>
        supabase
          .from("quote_reminder_cycles")
          .select(
            `
          id, case_id, thread_id, quote_ids, upload_group_id, status, current_step, next_step,
          next_reminder_at, last_quote_sent_at, last_client_response_at, last_staff_response_at,
          last_reminder_sent_at, last_error, whatsapp_task_due_at, whatsapp_status, whatsapp_message,
          stopped_reason, rejection_reason, rejection_comment, unsubscribed_at, rejected_at,
          completed_at, paused_at, created_at, updated_at,
          cases (
            id, title, company_name, client_first_name, client_last_name, client_email, client_phone,
            center, brand, event_date, participants, pax, status
          )
        `
          )
          .order("updated_at", { ascending: false })
          .order("id", { ascending: true })
          .range(from, to)
          .returns<CycleRow[]>()
      );

      const cycleRows = cycleData.filter((cycle) => cycle.cases);
      const caseIds = Array.from(new Set(cycleRows.map((cycle) => cycle.case_id)));
      const quoteIds = Array.from(
        new Set(cycleRows.flatMap((cycle) => (Array.isArray(cycle.quote_ids) ? cycle.quote_ids : [])))
      );

      // Filtres `.in()` découpés en lots : avec ~600 dossiers, l'URL dépassait la limite et PostgREST répondait 400.
      const [logsData, quotesData] = await Promise.all([
        fetchAllByIds<ReminderLog>(caseIds, (ids, from, to) =>
          supabase
            .from("reminder_logs")
            .select(
              "id, case_id, quote_id, channel, reminder_step, template_key, subject, email_sent, email_id, error_message, sent_at, created_at"
            )
            .eq("reminder_type", "quote_no_reply")
            .in("case_id", ids)
            .order("sent_at", { ascending: false })
            .order("id", { ascending: true })
            .range(from, to)
            .returns<ReminderLog[]>()
        ),
        fetchAllByIds<QuoteRow>(quoteIds, (ids, from, to) =>
          supabase
            .from("quotes")
            .select("id, display_name, version, signature_token, signed, signed_at, file_url, amount_ht")
            .in("id", ids)
            .is("archived_at", null)
            .order("id", { ascending: true })
            .range(from, to)
            .returns<QuoteRow[]>()
        ),
      ]);

      // Les lots sont fusionnés : on retrie comme Postgres (récents en premier, NULL en tête).
      const sortedLogs = [...logsData].sort((left, right) => (right.sent_at ?? "\uffff").localeCompare(left.sent_at ?? "\uffff"));
      const logsByCase = sortedLogs.reduce((map, log) => {
        const existing = map.get(log.case_id) ?? [];
        existing.push(log);
        map.set(log.case_id, existing);
        return map;
      }, new Map<string, ReminderLog[]>());
      const quotesById = new Map(quotesData.map((quote) => [quote.id, quote]));

      const hydrated = cycleRows.map((cycle) => {
        const quotes = (cycle.quote_ids ?? [])
          .map((quoteId) => quotesById.get(quoteId))
          .filter((quote): quote is QuoteRow => Boolean(quote));
        const amountHt =
          quotes
            .map((quote) => quote.amount_ht)
            .filter((amount): amount is number => amount !== null)
            .sort((left, right) => right - left)[0] ?? null;

        return {
          ...cycle,
          caseRecord: cycle.cases as CaseRow,
          logs: logsByCase.get(cycle.case_id) ?? [],
          quotes,
          amountHt,
        };
      });

      setCycles(hydrated);
      setSelectedCycleId((current) => {
        if (current && hydrated.some((cycle) => cycle.id === current)) return current;
        return hydrated[0]?.id ?? null;
      });
    } catch (err) {
      console.error("Erreur chargement relances:", err);
      setError(err instanceof Error ? err.message : "Erreur lors du chargement des relances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const centers = useMemo(() => {
    const values = cycles
      .filter((cycle) => getCycleBrand(cycle) === brand)
      .map((cycle) => cycle.caseRecord.center)
      .filter((center): center is string => Boolean(center));
    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right, "fr"));
  }, [cycles, brand]);

  useEffect(() => {
    setSelectedCenter("all");
  }, [brand]);

  const filteredCycles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return cycles.filter((cycle) => {
      if (getCycleBrand(cycle) !== brand) return false;
      if (selectedCenter !== "all" && cycle.caseRecord.center !== selectedCenter) return false;
      const bucket = getCycleBucket(cycle, Date.now());
      if (selectedView === "to_watch" && !["due", "errors"].includes(bucket)) return false;
      if (selectedView === "manual" && getPhoneLogs(cycle).length === 0) {
        return false;
      }
      if (selectedView === "waiting" && cycle.status !== "waiting_staff") return false;
      if (selectedView === "stopped" && !["completed", "unsubscribed", "rejected"].includes(cycle.status)) return false;
      if (!query) return true;
      return [
        getCaseTitle(cycle),
        getClientName(cycle),
        cycle.caseRecord.client_email || "",
        cycle.caseRecord.client_phone || "",
        cycle.caseRecord.center || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [cycles, brand, selectedCenter, selectedView, searchQuery]);

  const nowMs = Date.now();
  const buckets = useMemo(() => {
    return BUCKETS.reduce((acc, bucket) => {
      acc[bucket.key] = filteredCycles.filter((cycle) => getCycleBucket(cycle, nowMs) === bucket.key);
      return acc;
    }, {} as Record<BucketKey, CycleView[]>);
  }, [filteredCycles, nowMs]);

  const selectedCycle = filteredCycles.find((cycle) => cycle.id === selectedCycleId) ?? filteredCycles[0] ?? null;
  const totalEmailSent = useMemo(
    () => filteredCycles.reduce((count, cycle) => count + getSuccessfulEmailLogs(cycle).length, 0),
    [filteredCycles]
  );
  const totalPhoneDone = useMemo(
    () => filteredCycles.filter((cycle) => getPhoneLogs(cycle).length > 0).length,
    [filteredCycles]
  );

  const updateCycle = async (cycle: CycleView, updates: Record<string, unknown>, actionLabel: string) => {
    setSavingAction(actionLabel);
    try {
      const { error: updateError } = await supabase
        .from("quote_reminder_cycles")
        .update(updates)
        .eq("id", cycle.id);
      if (updateError) throw updateError;
      await fetchCycles();
    } catch (err) {
      console.error("Erreur action relance:", err);
      setError(err instanceof Error ? err.message : "Erreur lors de l'action");
    } finally {
      setSavingAction(null);
    }
  };

  const markPhoneCallDone = async (cycle: CycleView) => {
    setSavingAction(`phone-${cycle.id}`);
    try {
      const nowIso = new Date().toISOString();
      const step = getPhoneStep(cycle);
      const { error: logError } = await supabase.from("reminder_logs").insert({
        reminder_type: "quote_no_reply",
        case_id: cycle.case_id,
        quote_id: cycle.quote_ids[0] ?? null,
        channel: "phone",
        reminder_step: step,
        template_key: "quote_reminder_phone_call_done_v1",
        subject: "Appel de relance effectué",
        quote_ids: cycle.quote_ids,
        upload_group_id: cycle.upload_group_id,
        email_sent: true,
        sent_at: nowIso,
      });
      if (logError) throw logError;
      await fetchCycles();
    } catch (err) {
      console.error("Erreur appel relance:", err);
      setError(err instanceof Error ? err.message : "Erreur lors du marquage de l'appel");
    } finally {
      setSavingAction(null);
    }
  };

  const renderStepDots = (cycle: CycleView) => {
    return (
      <div className="flex items-center gap-1.5">
        {[1, 2, 3, 4].map((step) => {
          const log = getStepLog(cycle, step);
          const isCurrent = cycle.next_step === step && cycle.status === "active";
          const className = log?.email_sent
            ? "border-green-500 bg-green-500"
            : log?.error_message
              ? "border-red-500 bg-red-500"
              : isCurrent
                ? "border-amber-500 bg-amber-100"
                : "border-gray-300 bg-white";
          return (
            <span
              key={step}
              className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-semibold ${
                log?.email_sent || log?.error_message ? "text-white" : "text-gray-600"
              } ${className}`}
              title={`${STEP_META[step]?.label ?? `R${step}`} · ${STEP_META[step]?.angle ?? "Relance"}`}
            >
              {step}
            </span>
          );
        })}
      </div>
    );
  };

  const renderCycleRow = (cycle: CycleView) => {
    const isSelected = selectedCycle?.id === cycle.id;
    const nextStepMeta = STEP_META[cycle.next_step] ?? STEP_META[4];
    const state = getReminderState(cycle, nowMs);
    const phoneLogs = getPhoneLogs(cycle);
    const whatsappLogs = getWhatsappLogs(cycle);
    const lastContact = getLastContactLog(cycle);
    const lastContactLabel = lastContact
      ? `${lastContact.channel === "phone" ? "Appel" : lastContact.channel === "whatsapp" ? "WhatsApp" : "Email"} · ${formatDateTime(lastContact.sent_at || lastContact.created_at)}`
      : "Aucun contact noté";

    return (
      <tr
        key={cycle.id}
        className={`border-b border-gray-100 align-top ${isSelected ? "bg-indigo-50/70" : "bg-white hover:bg-gray-50"}`}
      >
        <td className="w-[30%] px-4 py-3">
          <button
            type="button"
            onClick={() => setSelectedCycleId(cycle.id)}
            className="block max-w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <span className="block truncate text-sm font-semibold text-gray-900">{getCaseTitle(cycle)}</span>
            <span className="mt-0.5 block truncate text-xs text-gray-600">
              {getClientName(cycle) || cycle.caseRecord.client_email || "Client non renseigné"}
            </span>
            <span className="mt-1 block truncate text-[11px] text-gray-500">
              {cycle.caseRecord.center || "Centre non renseigné"} · {formatMoney(cycle.amountHt)}
            </span>
          </button>
        </td>
        <td className="px-4 py-3">
          {renderStepDots(cycle)}
          <p className="mt-2 text-xs text-gray-500">
            {getSuccessfulEmailLogs(cycle).length}/4 emails envoyés
          </p>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${state.className}`}>
            {state.label}
          </span>
          <p className="mt-2 text-xs text-gray-500">
            {nextStepMeta?.label ?? "R4"} · {formatDateTime(cycle.next_reminder_at)}
          </p>
        </td>
        <td className="px-4 py-3 text-xs text-gray-600">
          <p>{lastContactLabel}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className={`rounded-full px-2 py-0.5 font-medium ${
              phoneLogs.length > 0 ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
            }`}>
              Appel {phoneLogs.length > 0 ? "fait" : "à noter"}
            </span>
            <span className={`rounded-full px-2 py-0.5 font-medium ${
              cycle.whatsapp_status === "pending"
                ? "bg-emerald-50 text-emerald-700"
                : whatsappLogs.length > 0
                  ? "bg-green-50 text-green-700"
                  : "bg-gray-100 text-gray-500"
            }`}>
              WhatsApp {cycle.whatsapp_status === "pending" ? "programmé" : whatsappLogs.length > 0 ? "envoyé" : "—"}
            </span>
          </div>
        </td>
        <td className="w-[150px] px-3 py-3">
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={savingAction !== null}
              onClick={() => markPhoneCallDone(cycle)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-gray-900 px-2 py-2 text-xs font-medium leading-snug text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 disabled:opacity-50"
            >
              <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
              Appel de relance effectué
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderEffectiveTimeline = (cycle: CycleView) => {
    const logs = [...cycle.logs]
      .filter((log) => log.sent_at || log.created_at)
      .sort(
        (left, right) =>
          new Date(right.sent_at || right.created_at).getTime() -
          new Date(left.sent_at || left.created_at).getTime()
      );

    if (logs.length === 0) {
      return <p className="rounded-md bg-gray-50 p-3 text-xs text-gray-500">Aucun contact noté pour ce dossier.</p>;
    }

    return (
      <div className="space-y-2">
        {logs.slice(0, 8).map((log) => {
          const channelLabel = log.channel === "phone" ? "Appel" : log.channel === "whatsapp" ? "WhatsApp" : "Email";
          const icon =
            log.channel === "phone" ? (
              <PhoneCall className="h-4 w-4 text-gray-700" aria-hidden="true" />
            ) : log.channel === "whatsapp" ? (
              <MessageCircle className="h-4 w-4 text-emerald-600" aria-hidden="true" />
            ) : log.email_sent ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600" aria-hidden="true" />
            );

          return (
            <div key={log.id} className="flex gap-3 rounded-md border border-gray-200 p-3">
              <div className="mt-0.5">{icon}</div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {channelLabel}
                  {log.reminder_step ? ` · R${log.reminder_step}` : ""}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {log.subject || log.error_message || "Contact enregistré"} · {formatDateTime(log.sent_at || log.created_at)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="border-b border-gray-200 bg-white">
        <div className="px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Relances devis</h1>
              {import.meta.env.VITE_QUOTE_REMINDERS_ENABLED !== "true" && (
                <p role="status" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Les envois automatiques de devis sont suspendus pendant la mise en service. Vous pouvez consulter le suivi et contacter vos clients manuellement.
                </p>
              )}
              <p className="mt-1 text-sm text-gray-600">
                Vue rapide de ce qui est prévu, à envoyer, à appeler ou déjà fait.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={fetchCycles}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Actualiser
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-5">
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase text-gray-500">Dossiers suivis</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">{filteredCycles.length}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase text-gray-500">Emails partis</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">{totalEmailSent}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase text-gray-500">Appels notés</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">{totalPhoneDone}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase text-gray-500">WhatsApp auto</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">{buckets.whatsapp?.length ?? 0}</p>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
              <p className="text-[11px] font-medium uppercase text-gray-500">À faire maintenant</p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {(buckets.due?.length ?? 0) + (buckets.errors?.length ?? 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div aria-live="polite" className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
          {error}
        </div>
      )}

      <div className="grid gap-4 p-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-1.5">
                {FOLLOW_UP_VIEWS.map((view) => (
                  <button
                    key={view.key}
                    type="button"
                    onClick={() => setSelectedView(view.key)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                      selectedView === view.key
                        ? "bg-gray-900 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {view.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor="relances-center-filter">Centre</label>
                <select
                  id="relances-center-filter"
                  name="center"
                  value={selectedCenter}
                  onChange={(event) => setSelectedCenter(event.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <option value="all">Tous les centres</option>
                  {centers.map((center) => (
                    <option key={center} value={center}>
                      {center}
                    </option>
                  ))}
                </select>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                  <label className="sr-only" htmlFor="relances-search">Rechercher une relance</label>
                  <input
                    id="relances-search"
                    name="search"
                    type="search"
                    autoComplete="off"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Chercher un client, dossier, email…"
                    className="w-80 rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">
              Chargement des dossiers…
            </div>
          ) : filteredCycles.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">Aucun dossier ne correspond aux filtres.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[820px] w-full border-collapse text-left">
                <thead className="bg-gray-50 text-[11px] font-semibold uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Dossier</th>
                    <th className="px-4 py-3">Emails</th>
                    <th className="px-4 py-3">État</th>
                    <th className="px-4 py-3">Dernier contact</th>
                    <th className="w-[150px] px-3 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>{filteredCycles.map(renderCycleRow)}</tbody>
              </table>
            </div>
          )}
        </div>

        <aside className="h-fit rounded-lg border border-gray-200 bg-white">
          {selectedCycle ? (
            <div>
              <div className="border-b border-gray-100 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Dossier choisi</p>
                    <h2 className="mt-1 truncate text-lg font-semibold text-gray-900">{getCaseTitle(selectedCycle)}</h2>
                    <p className="mt-1 truncate text-sm text-gray-600">
                      {getClientName(selectedCycle) || selectedCycle.caseRecord.client_email || "Client non renseigné"}
                    </p>
                  </div>
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                    {statusLabel(selectedCycle.status)}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={savingAction !== null}
                    onClick={() => markPhoneCallDone(selectedCycle)}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-gray-900 px-3 py-2 text-xs font-medium text-white hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 disabled:opacity-50"
                  >
                    <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
                    Appel de relance effectué
                  </button>
                  <a
                    href={`/case/${selectedCycle.case_id}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    Ouvrir dossier
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-gray-500">Événement</p>
                    <p className="mt-1 font-medium text-gray-900">{formatDateTime(selectedCycle.caseRecord.event_date)}</p>
                  </div>
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-gray-500">Montant devis</p>
                    <p className="mt-1 font-medium text-gray-900">{formatMoney(selectedCycle.amountHt)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 p-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Les 4 messages</h3>
                  <div className="mt-3 space-y-2">
                    {[1, 2, 3, 4].map((step) => {
                      const meta = STEP_META[step];
                      const log = getStepLog(selectedCycle, step);
                      const phoneLog = getStepLog(selectedCycle, step, "phone");
                      const whatsappLog = getStepLog(selectedCycle, step, "whatsapp");
                      const isCurrent = selectedCycle.next_step === step && selectedCycle.status === "active";
                      return (
                        <div key={step} className="flex gap-3 rounded-lg border border-gray-200 p-3">
                          <div className="mt-0.5">
                            {log?.email_sent ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : log?.error_message ? (
                              <AlertCircle className="h-4 w-4 text-red-600" />
                            ) : isCurrent ? (
                              <Clock className="h-4 w-4 text-amber-600" />
                            ) : (
                              <div className="h-4 w-4 rounded-full border border-gray-300" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {meta?.label ?? `R${step}`} · {meta?.angle ?? "Relance"}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {log?.email_sent
                                ? `Envoyée le ${formatDateTime(log.sent_at)}`
                                : log?.error_message
                                  ? log.error_message
                                  : isCurrent
                                    ? `Prévue le ${formatDateTime(selectedCycle.next_reminder_at)}`
                                    : "Non envoyée"}
                            </p>
                            {(phoneLog || whatsappLog) && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {phoneLog && (
                                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                    Appel effectué
                                  </span>
                                )}
                                {whatsappLog && (
                                  <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                                    WhatsApp envoyé
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedCycle.amountHt !== null && selectedCycle.amountHt >= 2000 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-semibold text-amber-900">Idée pour convaincre</p>
                    <p className="mt-1 text-xs leading-5 text-amber-800">
                      Devis supérieur à 2000 € HT : au prochain message, proposer un petit geste ou une option offerte.
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Historique</h3>
                  <div className="mt-3">{renderEffectiveTimeline(selectedCycle)}</div>
                </div>

                {selectedCycle.whatsapp_status === "pending" && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                      <MessageCircle className="h-4 w-4" aria-hidden="true" />
                      WhatsApp automatique prévu le {formatDateTime(selectedCycle.whatsapp_task_due_at)}
                    </div>
                    <p className="mt-2 text-xs leading-5 text-emerald-800">
                      Rien à faire : ce message partira automatiquement.
                    </p>
                    <textarea
                      readOnly
                      value={selectedCycle.whatsapp_message || ""}
                      rows={5}
                      className="mt-3 w-full rounded-md border border-emerald-200 bg-white p-2 text-xs text-gray-800"
                    />
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Pause / reprise</h3>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      disabled={savingAction !== null || selectedCycle.status === "paused"}
                      onClick={() =>
                        updateCycle(
                          selectedCycle,
                          { status: "paused", paused_at: new Date().toISOString(), next_reminder_at: null },
                          "pause"
                        )
                      }
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Pause className="h-3.5 w-3.5" aria-hidden="true" />
                      Pause
                    </button>
                    <button
                      type="button"
                      disabled={savingAction !== null || selectedCycle.status === "active"}
                      onClick={() =>
                        updateCycle(
                          selectedCycle,
                          {
                            status: "active",
                            next_reminder_at: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
                            paused_at: null,
                            last_error: null,
                          },
                          "resume"
                        )
                      }
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Play className="h-3.5 w-3.5" aria-hidden="true" />
                      Reprendre
                    </button>
                    <button
                      type="button"
                      disabled={savingAction !== null}
                      onClick={() =>
                        updateCycle(
                          selectedCycle,
                          { status: "active", next_reminder_at: new Date().toISOString(), last_error: null },
                          "send-now"
                        )
                      }
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Send className="h-3.5 w-3.5" aria-hidden="true" />
                      À envoyer maintenant
                    </button>
                  </div>
                </div>

                {selectedCycle.rejection_reason && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-semibold text-red-900">Motif de refus</p>
                    <p className="mt-1 text-xs text-red-800">{selectedCycle.rejection_reason}</p>
                    {selectedCycle.rejection_comment && (
                      <p className="mt-2 text-xs text-red-800">{selectedCycle.rejection_comment}</p>
                    )}
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Devis</h3>
                  <div className="mt-2 space-y-2">
                    {selectedCycle.quotes.map((quote) => (
                      <a
                        key={quote.id}
                        href={quote.signature_token ? `/sign/${quote.signature_token}` : quote.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {quote.display_name || `Version ${quote.version}`}
                        {quote.signed && <span className="ml-2 text-xs font-medium text-green-700">Signé</span>}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-sm text-gray-500">Sélectionnez un dossier.</div>
          )}
        </aside>
      </div>
    </div>
  );
}
